/**
 * uc-social.js — the social video row and its lightbox.
 *
 * Replaces the Instafeed app block. Data is `product.metafields.uc_social.videos`, emitted as
 * JSON by snippets/uc-social-videos.liquid. Read the contract notes at the top of that file
 * before changing anything here; the two must agree on which entries are shown, because the
 * lightbox addresses clips by index into the JSON.
 *
 * Deliberately a custom element and NOT wired into theme.js. An Impact upgrade replaces
 * theme.js wholesale, so store logic put there dies at the next version bump — the same reason
 * the auto-gift engine lives in custom.js. It also means the quick-view drawer gets this for
 * free: the drawer injects another product's #main, and custom elements upgrade on insertion.
 */
(function () {
  'use strict';

  if (customElements.get('uc-social-videos')) return;

  /**
   * Choose a rendition by the width it will actually be painted at.
   *
   * `sources` is sorted ascending by HEIGHT, which is not preference order, so taking first or
   * last is a bug. These are portrait clips: the 1080p rung is only 606px wide, so a naive
   * "biggest is best" both downloads too much and still upscales in a wide pane.
   *
   * Falls back to media_url (the 720p safe default) when there is nothing to choose between.
   */
  function pickSource(video, cssWidth) {
    var list = (video.sources || []).filter(function (s) {
      return s && s.url && s.width > 0;
    });
    if (!list.length) return video.media_url || null;

    var want = cssWidth * (window.devicePixelRatio || 1);
    var byWidth = list.slice().sort(function (a, b) {
      return a.width - b.width;
    });
    for (var i = 0; i < byWidth.length; i++) {
      if (byWidth[i].width >= want) return byWidth[i].url;
    }
    return byWidth[byWidth.length - 1].url;
  }

  /**
   * Ask Shopify's image CDN for the width we will paint at.
   *
   * Mirrors the same trick in the Liquid, for the lightbox rather than the tiles. Guarded to
   * `/s/files/` because `&width=` means nothing on any other host, and applied only to
   * POSTERS — never to a `sources` url, where a rendition that does not exist is a clip that
   * will not play.
   */
  function sizedPoster(url, width) {
    if (!url || url.indexOf('/s/files/') === -1 || url.indexOf('?') === -1) return url;
    return url + '&width=' + width;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /** Intl for 13764 -> "13,764". Nulls render nothing at all, never a zero. */
  function fmt(n) {
    if (typeof n !== 'number' || !isFinite(n)) return null;
    try {
      return n.toLocaleString();
    } catch (e) {
      return String(n);
    }
  }

  /**
   * Split an Instagram caption into its prose and its hashtags.
   *
   * Captions on this account end in a block of tags ("... #maw #vampire #darkfantasy"), which
   * read as noise in a sentence and as navigation when set apart — which is how Instagram
   * itself renders them, and what we are matching here.
   *
   * The \p{L}\p{N} class (rather than \w) keeps accented and non-Latin tags intact; \w would
   * cut #café to #caf.
   */
  var TAG_RE;
  try {
    TAG_RE = new RegExp('#[\\p{L}\\p{N}_]+', 'gu');
  } catch (e) {
    TAG_RE = /#[A-Za-z0-9_]+/g; // no Unicode property escapes (old Safari)
  }

  function splitCaption(text) {
    if (!text) return { body: '', tags: [] };
    var tags = text.match(TAG_RE) || [];
    var body = text.replace(TAG_RE, '').replace(/[ \t]{2,}/g, ' ').replace(/\n{2,}/g, '\n').trim();
    return { body: body, tags: tags };
  }

  class UcSocialVideos extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var dataEl = this.querySelector('[data-uc-social-data]');
      try {
        this.videos = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.videos = [];
      }
      if (!this.videos.length) return;

      this.track = this.querySelector('.uc-social__track');
      this.index = 0;
      this.lightbox = null;
      // Handed down from Liquid so they go through the theme's locales. Only keys that
      // actually exist are passed; see the note in uc-social-videos.liquid about why a
      // missing key does not fall back, it renders "Translation missing" verbatim.
      this.labels = {
        close: this.dataset.labelClose || 'Close',
        prev: this.dataset.labelPrev || 'Previous',
        next: this.dataset.labelNext || 'Next'
      };
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick.bind(this));

      this.observeFirstTile();

      if (this.track) {
        this.track.addEventListener('scroll', function () {
          this.updateNav();
          this.syncFirstPlayback();
        }.bind(this), { passive: true });
        window.addEventListener('resize', this.updateNav.bind(this), { passive: true });
        this.updateNav();
      }

      // The drawer reuses one container for every product it shows, so a lightbox left on
      // <body> would outlive the row that owns it.
      document.addEventListener('dialog:after-hide', this.close.bind(this));
    }

    disconnectedCallback() {
      // No history unwind on teardown: the drawer swaps products by replacing this element,
      // and a history.back() there navigates the page out from under its replacement.
      this.close(false);
      if (this.firstTileObserver) this.firstTileObserver.disconnect();
    }

    /**
     * Autoplay the first clip, muted, once the row scrolls into view.
     *
     * Deliberately only the first: five clips playing at once is five concurrent video
     * downloads and a page that sounds and looks like a fruit machine. The rest keep
     * preload="none" and stay posters until asked for.
     */
    observeFirstTile() {
      var cell = this.querySelector('[data-uc-social-first]');
      this.firstVideo = cell && cell.querySelector('video');
      if (!this.firstVideo || typeof IntersectionObserver === 'undefined') return;

      // An autoplaying clip is motion, so honour the setting that asks for less of it.
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // The autoplaying clip is ~1.4MB — far and away the heaviest thing this row can pull.
      // It is below the fold and gated on scrolling into view, so it costs nothing to Core
      // Web Vitals, but it is still real data. Don't spend it on someone who has asked their
      // browser to save data, or who is on a connection that will make a mess of it. They
      // keep the poster and the tap-to-play.
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || ''))) return;

      this.firstInView = false;
      this.firstTileObserver = new IntersectionObserver(function (entries) {
        this.firstInView = entries[entries.length - 1].isIntersecting;
        this.syncFirstPlayback();
      }.bind(this), { threshold: 0.4 });

      this.firstTileObserver.observe(cell);
    }

    /**
     * Play the first clip only while it is both on screen and still at the start of the row.
     *
     * Two conditions, because they answer to different gestures. Vertical page scroll is the
     * observer's job. Horizontal scroll of the track is NOT reliably its job: the row is two
     * cells wide on a phone, so cell 0 can be swiped most of the way out and still clear a
     * 0.4 threshold, leaving a video playing half off the edge of the screen. scrollLeft
     * settles it — off the start means paused.
     */
    syncFirstPlayback() {
      if (!this.firstVideo) return;
      var atStart = !this.track || this.track.scrollLeft <= 4;

      if (this.firstInView && atStart) {
        // The PROPERTY, not the attribute: Chrome gates autoplay on video.muted being true
        // at the moment play() is called, and an attribute set in markup can be out of step
        // with it after any earlier interaction.
        this.firstVideo.muted = true;
        var playing = this.firstVideo.play();
        // Rejects when the browser blocks it. Nothing to do but leave the poster up.
        if (playing && playing.catch) playing.catch(function () {});
      } else if (!this.firstVideo.paused) {
        this.firstVideo.pause();
      }
    }

    onClick(event) {
      var scroller = event.target.closest('[data-uc-social-scroll]');
      if (scroller && this.contains(scroller)) {
        this.scrollBy(parseInt(scroller.getAttribute('data-uc-social-scroll'), 10));
        return;
      }

      var opener = event.target.closest('[data-uc-social-open]');
      if (opener && this.contains(opener)) {
        event.preventDefault();
        this.open(parseInt(opener.getAttribute('data-uc-social-open'), 10));
      }
    }

    // --- row ---------------------------------------------------------------------------

    /**
     * Step the row, wrapping at both ends.
     *
     * Wrapped by jumping scrollLeft rather than by cloning cells. Cloning is the usual way to
     * get a seamless loop, but it would duplicate <video> elements — and the lightbox
     * addresses clips by index into the JSON, so a clone would either open the wrong clip or
     * need an index map that the Liquid and the JS both have to agree on. A jump is visible
     * and honest; a wrong video is not.
     */
    scrollBy(direction) {
      var cell = this.track && this.track.querySelector('.uc-social__cell');
      if (!cell) return;

      var step = cell.getBoundingClientRect().width + 12;
      // 2px of slack: sub-pixel layout means scrollLeft rarely reaches the exact maximum.
      var max = this.track.scrollWidth - this.track.clientWidth;
      var target;

      if (direction > 0 && this.track.scrollLeft >= max - 2) {
        target = 0;
      } else if (direction < 0 && this.track.scrollLeft <= 2) {
        target = max;
      } else {
        target = this.track.scrollLeft + step * direction;
      }

      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }

    updateNav() {
      if (!this.track) return;
      var prev = this.querySelector('.uc-social__nav--prev');
      var next = this.querySelector('.uc-social__nav--next');
      // Both arrows stay up whenever the row overflows, because both always do something —
      // the ends wrap. They hide only when every cell already fits and there is nowhere to go.
      var overflows = this.track.scrollWidth - this.track.clientWidth > 2;
      if (prev) prev.hidden = !overflows;
      if (next) next.hidden = !overflows;
    }

    // --- lightbox ----------------------------------------------------------------------

    open(index) {
      if (!this.videos[index]) return;
      this.index = index;
      this.opener = document.activeElement;

      if (!this.lightbox) this.buildLightbox();
      this.lightbox.hidden = false;

      // Only take the scroll lock if nobody else holds it — the quick-view drawer sets the
      // same flag, and stealing it means releasing a lock that is still wanted on close.
      this.tookScrollLock = !document.documentElement.classList.contains('uc-social-locked');
      if (this.tookScrollLock) document.documentElement.classList.add('uc-social-locked');

      document.addEventListener('keydown', this.onKeydown);

      // Make Back close the lightbox instead of leaving the page.
      //
      // On a phone the lightbox is full-screen, so it reads as a page and Back is the
      // obvious way out of it — but an overlay adds no history entry, so Back unwound the
      // navigation that got them to the product instead. One entry is pushed on open and
      // unwound on close, so the lightbox owns exactly one step of history however it is
      // dismissed.
      //
      // `pushState(state, '')` with no URL keeps the address bar exactly as it was: no
      // visible change, nothing new to crawl or share.
      this.historyPushed = false;
      try {
        history.pushState({ ucSocialLightbox: true }, '');
        this.historyPushed = true;
        window.addEventListener('popstate', this.onPopState);
      } catch (e) {
        // Some embedded webviews refuse pushState. Back then behaves as it did before,
        // which is the old behaviour rather than a broken one.
      }

      this.render();
    }

    /**
     * Back was pressed while open. The entry is already gone, so close WITHOUT unwinding —
     * calling history.back() here would eat a second entry and take them off the product.
     */
    onPopState() {
      this.historyPushed = false;
      this.close();
    }

    /**
     * @param {boolean} [unwind=true] false when the element is being torn down (the drawer
     *   swapping products), where touching history would navigate the page out from under
     *   whatever is replacing us.
     */
    close(unwind) {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.stopVideo();
      this.lightbox.hidden = true;
      if (this.tookScrollLock) document.documentElement.classList.remove('uc-social-locked');
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);

      // Listener removed FIRST: history.back() fires popstate asynchronously, and with the
      // listener still attached onPopState would call close() again.
      if (this.historyPushed && unwind !== false) {
        this.historyPushed = false;
        history.back();
      }

      if (this.opener && this.opener.focus) this.opener.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
      else if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'ArrowLeft') this.step(-1);
    }

    step(direction) {
      var next = this.index + direction;
      if (next < 0 || next >= this.videos.length) return;
      this.index = next;
      this.render();
    }

    buildLightbox() {
      var lb = el('div', 'uc-social-lb');
      lb.hidden = true;
      lb.innerHTML = [
        '<div class="uc-social-lb__backdrop" data-uc-lb-close></div>',
        '<div class="uc-social-lb__dialog" role="dialog" aria-modal="true" aria-label="Social video">',
        '  <button type="button" class="uc-social-lb__close" data-uc-lb-close aria-label="' + this.labels.close + '">',
        '    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg>',
        '  </button>',
        // The arrows live INSIDE the media pane, not the dialog. Centred on the dialog they
        // land on the boundary between video and panel once it stacks on mobile, which is
        // exactly where "View on Instagram" sits — the arrow covered the link. Inside the
        // pane they are always over the video at every breakpoint.
        // `__stage` exists so render() can clear the video without taking the arrows and
        // the mute button with it.
        '  <div class="uc-social-lb__media">',
        '    <div class="uc-social-lb__stage"></div>',
        '    <button type="button" class="uc-social-lb__nav uc-social-lb__nav--prev" data-uc-lb-step="-1" aria-label="' + this.labels.prev + '">',
        '      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>',
        '    </button>',
        '    <button type="button" class="uc-social-lb__nav uc-social-lb__nav--next" data-uc-lb-step="1" aria-label="' + this.labels.next + '">',
        '      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>',
        '    </button>',
        '  </div>',
        '  <aside class="uc-social-lb__panel">',
        '    <div class="uc-social-lb__comment"></div>',
        '    <div class="uc-social-lb__product-slot"></div>',
        '    <footer class="uc-social-lb__footer"></footer>',
        '  </aside>',
        '</div>'
      ].join('');

      lb.addEventListener('click', function (event) {
        if (event.target.closest('[data-uc-lb-close]')) {
          this.close();
          return;
        }
        var stepper = event.target.closest('[data-uc-lb-step]');
        if (stepper) this.step(parseInt(stepper.getAttribute('data-uc-lb-step'), 10));
        // A "Shop now" on the product you are already looking at should not reload the page.
        var shop = event.target.closest('[data-uc-social-shop]');
        if (shop && window.location.pathname === new URL(shop.href, window.location).pathname) {
          event.preventDefault();
          this.close();
          var buy = document.querySelector('.product-info__buy-buttons');
          if (buy) buy.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }.bind(this));

      // Appended to <body>, not left in place: the product-info column is a transformed,
      // overflow-clipped grid child, and a fixed-position child of one is clipped to it.
      document.body.appendChild(lb);
      this.lightbox = lb;
    }

    /**
     * The product a clip stands for, under the caption. Only entries from a collection or the
     * gallery carry `product` (sections/uc-collection-videos.liquid); on a product page the row
     * is already that product, so nothing renders there — the reason the card was removed from
     * the PDP lightbox in the first place.
     *
     * Linked by HANDLE, under the locale root the section passes as data-root. Title and image
     * are a weekly snapshot from UncoverOps; the handle is what a rename does not change.
     */
    renderProduct(video) {
      var slot = this.lightbox.querySelector('.uc-social-lb__product-slot');
      slot.innerHTML = '';
      var p = video.product;
      if (!p || !p.handle) return;

      var root = (this.dataset.root || '/').replace(/\/$/, '');
      var link = el('a', 'uc-social-lb__product');
      link.href = root + '/products/' + encodeURIComponent(p.handle);
      link.setAttribute('data-uc-social-shop', '');

      if (p.image_url) {
        var img = document.createElement('img');
        // Shopify's image CDN only; anywhere else &width= means nothing.
        img.src = p.image_url + (p.image_url.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        img.alt = '';
        img.width = 56;
        img.height = 56;
        link.appendChild(img);
      }

      var text = el('span', 'uc-social-lb__product-text');
      text.appendChild(el('span', 'uc-social-lb__product-title', p.title || ''));
      text.appendChild(el('span', 'uc-social-lb__product-cta', this.dataset.labelShop || 'View product'));
      link.appendChild(text);
      slot.appendChild(link);
    }

    stopVideo() {
      var v = this.lightbox && this.lightbox.querySelector('.uc-social-lb__stage video');
      if (v) {
        v.pause();
        // Detaching the source stops the buffer download too; pause alone keeps it going.
        v.removeAttribute('src');
        v.load();
      }
    }

    /**
     * The caption, laid out the way Instagram lays out a comment: avatar, handle in bold,
     * prose running straight on from it, hashtags set apart underneath as links.
     *
     * Built with DOM calls rather than innerHTML because every value here is caption text
     * from the metafield. It is our own data, but it is free text that reaches the page
     * verbatim, and textContent removes the whole question.
     */
    renderComment(video) {
      var host = this.lightbox.querySelector('.uc-social-lb__comment');
      host.innerHTML = '';
      if (!video.account && !video.caption) {
        host.hidden = true;
        return;
      }
      host.hidden = false;

      var avatarSrc = this.dataset.avatar;
      if (avatarSrc) {
        var img = document.createElement('img');
        img.className = 'uc-social-lb__avatar';
        img.src = avatarSrc;
        img.alt = '';
        img.width = 32;
        img.height = 32;
        host.appendChild(img);
      } else if (video.account) {
        host.appendChild(el('span', 'uc-social-lb__avatar uc-social-lb__avatar--initial',
          video.account.charAt(0).toUpperCase()));
      }

      var body = el('div', 'uc-social-lb__comment-body');

      if (video.account) {
        var handle = document.createElement('a');
        handle.className = 'uc-social-lb__handle';
        handle.href = 'https://www.instagram.com/' + encodeURIComponent(video.account) + '/';
        handle.target = '_blank';
        handle.rel = 'noopener nofollow';
        handle.textContent = video.account;
        body.appendChild(handle);
        body.appendChild(document.createTextNode(' '));
      }

      var parts = splitCaption(video.caption);
      if (parts.body) body.appendChild(el('span', 'uc-social-lb__text', parts.body));

      if (parts.tags.length) {
        var tagWrap = el('div', 'uc-social-lb__tags');
        parts.tags.forEach(function (tag, n) {
          var a = document.createElement('a');
          a.href = 'https://www.instagram.com/explore/tags/' + encodeURIComponent(tag.slice(1)) + '/';
          a.target = '_blank';
          a.rel = 'noopener nofollow';
          a.textContent = tag;
          if (n) tagWrap.appendChild(document.createTextNode(' '));
          tagWrap.appendChild(a);
        });
        body.appendChild(tagWrap);
      }

      host.appendChild(body);
    }

    render() {
      var video = this.videos[this.index];
      var lb = this.lightbox;

      this.stopVideo();

      var media = lb.querySelector('.uc-social-lb__stage');
      media.innerHTML = '';

      // Measure the pane before choosing a rendition, so the source matches the paint size.
      // Falls back to 404 (the 720p rung) before first layout.
      var paneWidth = media.getBoundingClientRect().width || 404;
      var src = pickSource(video, paneWidth);

      var best = (video.sources || []).slice().sort(function (a, b) { return b.height - a.height; })[0];
      var ratio = best && best.width && best.height ? best.width / best.height : 0.5625;

      // The theme's own player. `group="product"` is what pauses the gallery clip when this
      // opens — two videos playing at once on one page is the thing that looks broken.
      var wrapper = document.createElement('video-media');
      wrapper.setAttribute('autoplay', '');
      wrapper.setAttribute('group', 'product');
      wrapper.style.setProperty('--aspect-ratio', String(ratio));

      var v = document.createElement('video');
      v.setAttribute('controls', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('loop', '');
      v.muted = true;
      // preload="auto" here and only here: the visitor has asked for this one clip by name.
      v.setAttribute('preload', 'auto');
      if (video.image_url) v.setAttribute('poster', sizedPoster(video.image_url, 800));
      if (src) {
        var source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        v.appendChild(source);
      }
      wrapper.appendChild(v);
      media.appendChild(wrapper);

      // hls_url is deliberately NOT offered as a <source> beside the mp4s: it plays on Safari
      // and fails silently everywhere else, and the failure is a black frame with no error.
      // If adaptive bitrate is ever wanted it goes through hls.js, loaded on first play.

      var mute = el('button', 'uc-social-lb__mute');
      mute.type = 'button';
      mute.setAttribute('aria-label', 'Unmute');
      mute.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3z"/><path d="M16 8l5 5m0-5l-5 5" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
      mute.addEventListener('click', function () {
        v.muted = !v.muted;
        mute.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
        mute.classList.toggle('is-unmuted', !v.muted);
      });
      media.appendChild(mute);

      this.renderComment(video);
      this.renderProduct(video);

      var footer = lb.querySelector('.uc-social-lb__footer');
      footer.innerHTML = '';
      var likes = fmt(video.likes);
      var views = fmt(video.views);
      if (likes || views) {
        var stats = el('span', 'uc-social-lb__stats');
        if (likes) {
          var heart = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          heart.setAttribute('class', 'uc-social-lb__heart');
          heart.setAttribute('viewBox', '0 0 24 24');
          heart.setAttribute('width', '14');
          heart.setAttribute('height', '14');
          heart.setAttribute('fill', 'currentColor');
          heart.setAttribute('aria-hidden', 'true');
          var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M12 21s-7.5-4.6-9.6-9A5.2 5.2 0 0 1 12 6.1a5.2 5.2 0 0 1 9.6 5.9C19.5 16.4 12 21 12 21z');
          heart.appendChild(path);
          stats.appendChild(heart);
          stats.appendChild(document.createTextNode(' ' + likes));
        }
        if (views) {
          if (likes) stats.appendChild(document.createTextNode(' · '));
          stats.appendChild(document.createTextNode(views + ' views'));
        }
        footer.appendChild(stats);
      }

      // Null on scheduled posts — published to the metafield before they exist on Instagram.
      if (video.permalink) {
        var link = el('a', 'uc-social-lb__permalink', 'View on Instagram');
        link.href = video.permalink;
        link.target = '_blank';
        link.rel = 'noopener nofollow';
        footer.appendChild(link);
      }

      lb.querySelector('.uc-social-lb__nav--prev').hidden = this.index === 0;
      lb.querySelector('.uc-social-lb__nav--next').hidden = this.index >= this.videos.length - 1;

      lb.querySelector('.uc-social-lb__close').focus();
    }
  }

  customElements.define('uc-social-videos', UcSocialVideos);
})();
